import { useState, useEffect, useCallback } from "react";
import { supabase, StoreEntry, StoreExit, FoodRecord, MediaEntry, MediaExit, School, StoreTransfer, logActivity } from "@/lib/supabase";
import { t } from "@/lib/i18n";
import { Warehouse, Utensils, Camera, Share, Package, Plus, Trash2, ArrowLeftRight, School as SchoolIcon, Building2 } from "lucide-react";

type MgmtTab = "store" | "food" | "media" | "transfers" | "schools";
type StoreTab = "entry" | "exit" | "inventory";
type FoodTab = "ubuntu" | "eagles" | "bakery";
type MediaTab = "entry" | "exit" | "inventory";

export default function Management() {
  const [mgmtTab, setMgmtTab] = useState<MgmtTab>("store");
  const [storeTab, setStoreTab] = useState<StoreTab>("entry");
  const [foodTab, setFoodTab] = useState<FoodTab>("ubuntu");
  const [mediaTab, setMediaTab] = useState<MediaTab>("entry");

  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");

  const [transfers, setTransfers] = useState<StoreTransfer[]>([]);
  const [trFromSchool, setTrFromSchool] = useState("");
  const [trToSchool, setTrToSchool] = useState("");
  const [trItem, setTrItem] = useState("");
  const [trDesc, setTrDesc] = useState("");
  const [trQty, setTrQty] = useState("");

  const [newSchoolName, setNewSchoolName] = useState("");
  const [newSchoolLocation, setNewSchoolLocation] = useState("");
  const [newSchoolDesc, setNewSchoolDesc] = useState("");

  const [storeEntries, setStoreEntries] = useState<StoreEntry[]>([]);
  const [storeExits, setStoreExits] = useState<StoreExit[]>([]);
  const [sEntryItem, setSEntryItem] = useState(""); const [sEntryDesc, setSEntryDesc] = useState(""); const [sEntryQty, setSEntryQty] = useState("");
  const [sExitItem, setSExitItem] = useState(""); const [sExitDesc, setSExitDesc] = useState(""); const [sExitQty, setSExitQty] = useState(""); const [sExitDest, setSExitDest] = useState("");

  const [foodRecords, setFoodRecords] = useState<FoodRecord[]>([]);
  const [fItem, setFItem] = useState(""); const [fQty, setFQty] = useState(""); const [fDetails, setFDetails] = useState("");
  const [fCollector, setFCollector] = useState(""); const [fDate, setFDate] = useState("");

  const [mediaEntries, setMediaEntries] = useState<MediaEntry[]>([]);
  const [mediaExits, setMediaExits] = useState<MediaExit[]>([]);
  const [mEntryItem, setMEntryItem] = useState(""); const [mEntryDesc, setMEntryDesc] = useState(""); const [mEntryQty, setMEntryQty] = useState("");
  const [mExitItem, setMExitItem] = useState(""); const [mExitDesc, setMExitDesc] = useState(""); const [mExitQty, setMExitQty] = useState("");

  const fetchStoreEntries = useCallback(async () => {
    const { data } = await supabase.from("store_entries").select("*").order("created_at", { ascending: false });
    if (data) setStoreEntries(data as StoreEntry[]);
  }, []);
  const fetchStoreExits = useCallback(async () => {
    const { data } = await supabase.from("store_exits").select("*").order("created_at", { ascending: false });
    if (data) setStoreExits(data as StoreExit[]);
  }, []);
  const fetchFood = useCallback(async () => {
    const { data } = await supabase.from("food_records").select("*").order("created_at", { ascending: false });
    if (data) setFoodRecords(data as FoodRecord[]);
  }, []);
  const fetchMediaEntries = useCallback(async () => {
    const { data } = await supabase.from("media_entries").select("*").order("created_at", { ascending: false });
    if (data) setMediaEntries(data as MediaEntry[]);
  }, []);
  const fetchMediaExits = useCallback(async () => {
    const { data } = await supabase.from("media_exits").select("*").order("created_at", { ascending: false });
    if (data) setMediaExits(data as MediaExit[]);
  }, []);
  const fetchTransfers = useCallback(async () => {
    const { data } = await supabase.from("store_transfers").select("*").order("created_at", { ascending: false });
    if (data) setTransfers(data as StoreTransfer[]);
  }, []);
  const fetchSchools = useCallback(async () => {
    const { data } = await supabase.from("schools").select("*").order("name");
    if (data) {
      setSchools(data as School[]);
      if (data.length > 0 && !selectedSchoolId) setSelectedSchoolId((data[0] as School).id);
    }
  }, [selectedSchoolId]);

  useEffect(() => {
    fetchSchools();
    fetchStoreEntries(); fetchStoreExits(); fetchFood(); fetchMediaEntries(); fetchMediaExits(); fetchTransfers();
  }, [fetchSchools, fetchStoreEntries, fetchStoreExits, fetchFood, fetchMediaEntries, fetchMediaExits, fetchTransfers]);

  // Real-time subscriptions
  useEffect(() => {
    const channels = [
      supabase.channel("mgmt-store-entries").on("postgres_changes", { event: "*", schema: "public", table: "store_entries" }, () => fetchStoreEntries()).subscribe(),
      supabase.channel("mgmt-store-exits").on("postgres_changes", { event: "*", schema: "public", table: "store_exits" }, () => fetchStoreExits()).subscribe(),
      supabase.channel("mgmt-food").on("postgres_changes", { event: "*", schema: "public", table: "food_records" }, () => fetchFood()).subscribe(),
      supabase.channel("mgmt-media-entries").on("postgres_changes", { event: "*", schema: "public", table: "media_entries" }, () => fetchMediaEntries()).subscribe(),
      supabase.channel("mgmt-media-exits").on("postgres_changes", { event: "*", schema: "public", table: "media_exits" }, () => fetchMediaExits()).subscribe(),
      supabase.channel("mgmt-transfers").on("postgres_changes", { event: "*", schema: "public", table: "store_transfers" }, () => fetchTransfers()).subscribe(),
      supabase.channel("mgmt-schools").on("postgres_changes", { event: "*", schema: "public", table: "schools" }, () => fetchSchools()).subscribe(),
    ];
    return () => { channels.forEach((ch) => supabase.removeChannel(ch)); };
  }, [fetchStoreEntries, fetchStoreExits, fetchFood, fetchMediaEntries, fetchMediaExits, fetchTransfers, fetchSchools]);

  const addStoreEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    await supabase.from("store_entries").insert({ item: sEntryItem, description: sEntryDesc, quantity: parseInt(sEntryQty), school_id: selectedSchoolId || null });
    await logActivity("added store entry", "store_entry", undefined, sEntryItem, selectedSchoolId, `Qty: ${sEntryQty}`);
    setSEntryItem(""); setSEntryDesc(""); setSEntryQty("");
    fetchStoreEntries();
  };
  const addStoreExit = async (e: React.FormEvent) => {
    e.preventDefault();
    await supabase.from("store_exits").insert({ item: sExitItem, description: sExitDesc, quantity: parseInt(sExitQty), destination: sExitDest, school_id: selectedSchoolId || null });
    await logActivity("recorded store exit", "store_exit", undefined, sExitItem, selectedSchoolId, `Qty: ${sExitQty} -> ${sExitDest}`);
    setSExitItem(""); setSExitDesc(""); setSExitQty(""); setSExitDest("");
    fetchStoreExits();
  };
  const deleteStoreEntry = async (id: string) => { await supabase.from("store_entries").delete().eq("id", id); fetchStoreEntries(); };
  const deleteStoreExit = async (id: string) => { await supabase.from("store_exits").delete().eq("id", id); fetchStoreExits(); };

  const addFoodRecord = async (e: React.FormEvent, section: FoodTab) => {
    e.preventDefault();
    if (section === "bakery") {
      await supabase.from("food_records").insert({ section: "bakery", item: fItem, quantity: fQty, collector: fCollector, record_date: fDate || undefined, school_id: selectedSchoolId || null });
    } else {
      await supabase.from("food_records").insert({ section, item: fItem, quantity: fQty, details: fDetails, school_id: selectedSchoolId || null });
    }
    await logActivity(`added ${section} food record`, "food_record", undefined, fItem, selectedSchoolId, `Qty: ${fQty}`);
    setFItem(""); setFQty(""); setFDetails(""); setFCollector(""); setFDate("");
    fetchFood();
  };
  const deleteFood = async (id: string) => { await supabase.from("food_records").delete().eq("id", id); fetchFood(); };

  const addMediaEntry = async (e: React.FormEvent) => {
    e.preventDefault();
    await supabase.from("media_entries").insert({ item: mEntryItem, description: mEntryDesc, quantity: parseInt(mEntryQty), school_id: selectedSchoolId || null });
    await logActivity("added media entry", "media_entry", undefined, mEntryItem, selectedSchoolId, `Qty: ${mEntryQty}`);
    setMEntryItem(""); setMEntryDesc(""); setMEntryQty("");
    fetchMediaEntries();
  };
  const addMediaExit = async (e: React.FormEvent) => {
    e.preventDefault();
    await supabase.from("media_exits").insert({ item: mExitItem, description: mExitDesc, quantity: parseInt(mExitQty), school_id: selectedSchoolId || null });
    await logActivity("recorded media exit", "media_exit", undefined, mExitItem, selectedSchoolId, `Qty: ${mExitQty}`);
    setMExitItem(""); setMExitDesc(""); setMExitQty("");
    fetchMediaExits();
  };
  const deleteMediaEntry = async (id: string) => { await supabase.from("media_entries").delete().eq("id", id); fetchMediaEntries(); };
  const deleteMediaExit = async (id: string) => { await supabase.from("media_exits").delete().eq("id", id); fetchMediaExits(); };

  const calcStoreInventory = () => {
    const summary: Record<string, { item: string; desc: string; totalEntry: number; totalExit: number }> = {};
    storeEntries.forEach((e) => {
      const key = `${e.item.toLowerCase()}|${e.description.toLowerCase()}`;
      if (!summary[key]) summary[key] = { item: e.item, desc: e.description, totalEntry: 0, totalExit: 0 };
      summary[key].totalEntry += e.quantity;
    });
    storeExits.forEach((ex) => {
      const key = `${ex.item.toLowerCase()}|${ex.description.toLowerCase()}`;
      if (!summary[key]) summary[key] = { item: ex.item, desc: ex.description, totalEntry: 0, totalExit: 0 };
      summary[key].totalExit += ex.quantity;
    });
    return summary;
  };

  const calcMediaInventory = () => {
    const summary: Record<string, { item: string; desc: string; totalEntry: number; totalExit: number }> = {};
    mediaEntries.forEach((e) => {
      const key = `${e.item.toLowerCase()}|${e.description.toLowerCase()}`;
      if (!summary[key]) summary[key] = { item: e.item, desc: e.description, totalEntry: 0, totalExit: 0 };
      summary[key].totalEntry += e.quantity;
    });
    mediaExits.forEach((ex) => {
      const key = `${ex.item.toLowerCase()}|${ex.description.toLowerCase()}`;
      if (!summary[key]) summary[key] = { item: ex.item, desc: ex.description, totalEntry: 0, totalExit: 0 };
      summary[key].totalExit += ex.quantity;
    });
    return summary;
  };

  const addTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data } = await supabase.from("store_transfers").insert({
      from_school_id: trFromSchool || null,
      to_school_id: trToSchool || null,
      item: trItem,
      description: trDesc,
      quantity: parseInt(trQty),
    }).select().single();
    const fromName = schools.find((s) => s.id === trFromSchool)?.name || "?";
    const toName = schools.find((s) => s.id === trToSchool)?.name || "?";
    await logActivity("transferred materials", "store_transfer", data?.id, trItem, undefined, `${trQty} x ${trItem}: ${fromName} -> ${toName}`);
    setTrItem(""); setTrDesc(""); setTrQty(""); setTrFromSchool(""); setTrToSchool("");
    fetchTransfers();
  };

  const addSchool = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data } = await supabase.from("schools").insert({
      name: newSchoolName.trim(),
      location: newSchoolLocation || null,
      description: newSchoolDesc || null,
    }).select().single();
    await logActivity("created school", "school", data?.id, newSchoolName.trim(), undefined, newSchoolLocation || "");
    setNewSchoolName(""); setNewSchoolLocation(""); setNewSchoolDesc("");
    fetchSchools();
  };

  const deleteSchool = async (id: string) => {
    await supabase.from("schools").delete().eq("id", id);
    await logActivity("deleted school", "school", id, undefined, undefined, "");
    fetchSchools();
  };

  const storeInv = calcStoreInventory();
  const mediaInv = calcMediaInventory();
  const foodBySection = (s: FoodTab) => foodRecords.filter((r) => r.section === s);

  return (
    <div className="animate-fadeIn">
      <div className="flex gap-2.5 mb-6 flex-wrap">
        <button className={mgmtTab === "store" ? "btn-primary" : "btn-cyan"} onClick={() => setMgmtTab("store")}><Warehouse className="w-5 h-5" /> {t("mgmt_tab_store")}</button>
        <button className={mgmtTab === "food" ? "btn-primary" : "btn-cyan"} onClick={() => setMgmtTab("food")}><Utensils className="w-5 h-5" /> {t("mgmt_tab_food")}</button>
        <button className={mgmtTab === "media" ? "btn-primary" : "btn-cyan"} onClick={() => setMgmtTab("media")}><Camera className="w-5 h-5" /> {t("mgmt_tab_media")}</button>
        <button className={mgmtTab === "transfers" ? "btn-primary" : "btn-cyan"} onClick={() => setMgmtTab("transfers")}><ArrowLeftRight className="w-5 h-5" /> {t("mgmt_tab_transfers")}</button>
        <button className={mgmtTab === "schools" ? "btn-primary" : "btn-cyan"} onClick={() => setMgmtTab("schools")}><Building2 className="w-5 h-5" /> {t("mgmt_tab_schools")}</button>
      </div>

      {mgmtTab !== "transfers" && (
        <div className="card mb-5 p-4">
          <div className="form-group">
            <label className="form-label">{t("lbl_select_school")}</label>
            <div className="relative">
              <SchoolIcon className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
              <select className="form-input pl-10" value={selectedSchoolId} onChange={(e) => setSelectedSchoolId(e.target.value)}>
                <option value="">{t("lbl_all_schools")}</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* STORE */}
      {mgmtTab === "store" && (
        <div className="card">
          <div className="mb-5 pb-3 border-b-2 border-gray-200">
            <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold"><Warehouse className="w-6 h-6" /> {t("store_title")}</h2>
          </div>
          <div className="flex gap-2.5 mb-5 flex-wrap">
            <button className="btn-cyan btn-sm" onClick={() => setStoreTab("entry")}>{t("btn_entry")}</button>
            <button className="btn-warning btn-sm" onClick={() => setStoreTab("exit")}>{t("btn_exit")}</button>
            <button className="btn-success btn-sm" onClick={() => setStoreTab("inventory")}>{t("btn_stock")}</button>
          </div>

          {storeTab === "entry" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("store_entry_reg")}</h3>
              <form onSubmit={addStoreEntry}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item")}</label><input className="form-input" value={sEntryItem} onChange={(e) => setSEntryItem(e.target.value)} required placeholder={t("ph_item")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_description")}</label><input className="form-input" value={sEntryDesc} onChange={(e) => setSEntryDesc(e.target.value)} required placeholder={t("ph_desc")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_quantity")}</label><input className="form-input" type="number" min="1" value={sEntryQty} onChange={(e) => setSEntryQty(e.target.value)} required placeholder={t("ph_qty_num")} /></div>
                </div>
                <button type="submit" className="btn-primary"><Plus className="w-4 h-4" /> {t("btn_record_entry")}</button>
              </form>
              <div className="mt-5">
                <h4 className="font-semibold mb-2">{t("store_all_entries")}</h4>
                {storeEntries.length === 0 ? <p className="text-gray-400 italic">{t("store_no_entries")}</p> : (
                  <div>{storeEntries.map((e) => (
                    <div key={e.id} className="attendance-row">
                      <div><strong>{e.item}</strong> - {e.description} ({t("lbl_quantity")}: {e.quantity}) | <em className="text-gray-400 text-xs">{t("lbl_date")}: {e.entry_date}</em></div>
                      <button className="btn-danger btn-sm" onClick={() => deleteStoreEntry(e.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {storeTab === "exit" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("store_exit_reg")}</h3>
              <form onSubmit={addStoreExit}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item")}</label><input className="form-input" value={sExitItem} onChange={(e) => setSExitItem(e.target.value)} required placeholder={t("ph_item")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_description")}</label><input className="form-input" value={sExitDesc} onChange={(e) => setSExitDesc(e.target.value)} required placeholder={t("ph_desc")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_quantity")}</label><input className="form-input" type="number" min="1" value={sExitQty} onChange={(e) => setSExitQty(e.target.value)} required placeholder={t("ph_qty_num")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_destination")}</label><input className="form-input" value={sExitDest} onChange={(e) => setSExitDest(e.target.value)} required placeholder={t("ph_dest")} /></div>
                </div>
                <button type="submit" className="btn-warning"><Share className="w-4 h-4" /> {t("btn_record_exit")}</button>
              </form>
              <div className="mt-5">
                <h4 className="font-semibold mb-2">{t("store_all_exits")}</h4>
                {storeExits.length === 0 ? <p className="text-gray-400 italic">{t("store_no_exits")}</p> : (
                  <div>{storeExits.map((ex) => (
                    <div key={ex.id} className="attendance-row">
                      <div><strong>{ex.item}</strong> - {ex.description} ({t("lbl_quantity")}: {ex.quantity}) &rarr; <em>{t("lbl_destination")}: {ex.destination}</em> | <em className="text-gray-400 text-xs">{t("lbl_date")}: {ex.exit_date}</em></div>
                      <button className="btn-danger btn-sm" onClick={() => deleteStoreExit(ex.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}</div>
                )}
              </div>
            </div>
          )}

          {storeTab === "inventory" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("store_stock_title")}</h3>
              <p className="text-gray-500 mb-4 text-sm">{t("store_stock_desc")}</p>
              {Object.keys(storeInv).length === 0 ? <p className="text-gray-400 italic">{t("store_no_inventory")}</p> : (
                <div>{Object.entries(storeInv).map(([key, d]) => {
                  const net = d.totalEntry - d.totalExit;
                  const color = net > 30 ? "text-[#2E7D32]" : net < 30 ? "text-[#D32F2F]" : "text-[#2B2D42]";
                  return (
                    <div key={key} className="attendance-row">
                      <div><strong>{d.item}</strong> ({d.desc})</div>
                      <div>{t("store_entries")} {d.totalEntry} | {t("store_exits")} {d.totalExit} | {t("store_stock")} <span className={`${color} font-bold`}>{net}</span></div>
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* FOOD */}
      {mgmtTab === "food" && (
        <div className="card">
          <div className="mb-5 pb-3 border-b-2 border-gray-200">
            <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold"><Utensils className="w-6 h-6" /> {t("food_title")}</h2>
          </div>
          <div className="flex gap-2.5 mb-5 flex-wrap">
            <button className="btn-cyan btn-sm" onClick={() => setFoodTab("ubuntu")}>{t("food_ubuntu")}</button>
            <button className="btn-primary btn-sm" onClick={() => setFoodTab("eagles")}>{t("food_eagles")}</button>
            <button className="btn-warning btn-sm" onClick={() => setFoodTab("bakery")}>{t("food_bakery")}</button>
          </div>

          {foodTab === "ubuntu" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("food_ubuntu_title")}</h3>
              <form onSubmit={(e) => addFoodRecord(e, "ubuntu")}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item_meal")}</label><input className="form-input" value={fItem} onChange={(e) => setFItem(e.target.value)} required placeholder={t("ph_food_ubuntu")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_qty_str")}</label><input className="form-input" value={fQty} onChange={(e) => setFQty(e.target.value)} required placeholder={t("ph_food_ubuntu_qty")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_receiver_details")}</label><input className="form-input" value={fDetails} onChange={(e) => setFDetails(e.target.value)} required placeholder={t("ph_food_ubuntu_details")} /></div>
                </div>
                <button type="submit" className="btn-primary">{t("btn_insert_ubuntu")}</button>
              </form>
              <div className="mt-4">{foodBySection("ubuntu").length === 0 ? <p className="text-gray-400 italic">{t("food_no_records")}</p> : foodBySection("ubuntu").map((r) => (
                <div key={r.id} className="attendance-row">
                  <div><strong>{r.item}</strong> - {t("food_qty")} {r.quantity} | {t("food_details")} {r.details}</div>
                  <button className="btn-danger btn-sm" onClick={() => deleteFood(r.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}</div>
            </div>
          )}

          {foodTab === "eagles" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("food_eagles_title")}</h3>
              <form onSubmit={(e) => addFoodRecord(e, "eagles")}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item_meal")}</label><input className="form-input" value={fItem} onChange={(e) => setFItem(e.target.value)} required placeholder={t("ph_food_eagles")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_qty_str")}</label><input className="form-input" value={fQty} onChange={(e) => setFQty(e.target.value)} required placeholder={t("ph_food_eagles_qty")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_receiver_details")}</label><input className="form-input" value={fDetails} onChange={(e) => setFDetails(e.target.value)} required placeholder={t("ph_food_eagles_details")} /></div>
                </div>
                <button type="submit" className="btn-primary">{t("btn_insert_eagles")}</button>
              </form>
              <div className="mt-4">{foodBySection("eagles").length === 0 ? <p className="text-gray-400 italic">{t("food_no_records")}</p> : foodBySection("eagles").map((r) => (
                <div key={r.id} className="attendance-row">
                  <div><strong>{r.item}</strong> - {t("food_qty")} {r.quantity} | {t("food_details")} {r.details}</div>
                  <button className="btn-danger btn-sm" onClick={() => deleteFood(r.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}</div>
            </div>
          )}

          {foodTab === "bakery" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("food_bakery_title")}</h3>
              <form onSubmit={(e) => addFoodRecord(e, "bakery")}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item")}</label><input className="form-input" value={fItem} onChange={(e) => setFItem(e.target.value)} required placeholder={t("ph_bakery_item")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_qty_str")}</label><input className="form-input" value={fQty} onChange={(e) => setFQty(e.target.value)} required placeholder={t("ph_bakery_qty")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_collector")}</label><input className="form-input" value={fCollector} onChange={(e) => setFCollector(e.target.value)} required placeholder={t("ph_bakery_collector")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_date")}</label><input className="form-input" type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} required /></div>
                </div>
                <button type="submit" className="btn-warning">{t("btn_insert_bakery")}</button>
              </form>
              <div className="mt-4">{foodBySection("bakery").length === 0 ? <p className="text-gray-400 italic">{t("food_no_records")}</p> : foodBySection("bakery").map((r) => (
                <div key={r.id} className="attendance-row">
                  <div>{t("food_item")} {r.item} | {t("food_qty")} {r.quantity} | {t("food_collector")} {r.collector} | {t("food_date")} {r.record_date}</div>
                  <button className="btn-danger btn-sm" onClick={() => deleteFood(r.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}</div>
            </div>
          )}
        </div>
      )}

      {/* MEDIA */}
      {mgmtTab === "media" && (
        <div className="card">
          <div className="mb-5 pb-3 border-b-2 border-gray-200">
            <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold"><Camera className="w-6 h-6" /> {t("media_title")}</h2>
          </div>
          <div className="flex gap-2.5 mb-5 flex-wrap">
            <button className="btn-cyan btn-sm" onClick={() => setMediaTab("entry")}>{t("btn_entry")}</button>
            <button className="btn-warning btn-sm" onClick={() => setMediaTab("exit")}>{t("btn_exit")}</button>
            <button className="btn-success btn-sm" onClick={() => setMediaTab("inventory")}>{t("btn_auto_inventory")}</button>
          </div>

          {mediaTab === "entry" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("media_entry_title")}</h3>
              <form onSubmit={addMediaEntry}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item")}</label><input className="form-input" value={mEntryItem} onChange={(e) => setMEntryItem(e.target.value)} required placeholder={t("ph_media_item")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_description")}</label><input className="form-input" value={mEntryDesc} onChange={(e) => setMEntryDesc(e.target.value)} required placeholder={t("ph_media_desc")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_quantity")}</label><input className="form-input" type="number" min="1" value={mEntryQty} onChange={(e) => setMEntryQty(e.target.value)} required placeholder={t("ph_media_qty")} /></div>
                </div>
                <button type="submit" className="btn-primary">{t("btn_add_media_entry")}</button>
              </form>
              <div className="mt-4">{mediaEntries.length === 0 ? <p className="text-gray-400 italic">{t("media_no_entries")}</p> : mediaEntries.map((m) => (
                <div key={m.id} className="attendance-row">
                  <div><strong>{m.item}</strong> - {m.description} ({t("lbl_quantity")}: {m.quantity}) | <em className="text-gray-400 text-xs">{t("lbl_date")}: {m.entry_date}</em></div>
                  <button className="btn-danger btn-sm" onClick={() => deleteMediaEntry(m.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}</div>
            </div>
          )}

          {mediaTab === "exit" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("media_exit_title")}</h3>
              <form onSubmit={addMediaExit}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                  <div className="form-group"><label className="form-label">{t("lbl_item")}</label><input className="form-input" value={mExitItem} onChange={(e) => setMExitItem(e.target.value)} required placeholder={t("ph_media_item")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_description")}</label><input className="form-input" value={mExitDesc} onChange={(e) => setMExitDesc(e.target.value)} required placeholder={t("ph_media_desc")} /></div>
                  <div className="form-group"><label className="form-label">{t("lbl_quantity")}</label><input className="form-input" type="number" min="1" value={mExitQty} onChange={(e) => setMExitQty(e.target.value)} required placeholder={t("ph_media_qty")} /></div>
                </div>
                <button type="submit" className="btn-warning">{t("btn_add_media_exit")}</button>
              </form>
              <div className="mt-4">{mediaExits.length === 0 ? <p className="text-gray-400 italic">{t("media_no_exits")}</p> : mediaExits.map((m) => (
                <div key={m.id} className="attendance-row">
                  <div><strong>{m.item}</strong> - {m.description} ({t("lbl_quantity")}: {m.quantity}) | <em className="text-gray-400 text-xs">{t("lbl_date")}: {m.exit_date}</em></div>
                  <button className="btn-danger btn-sm" onClick={() => deleteMediaExit(m.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}</div>
            </div>
          )}

          {mediaTab === "inventory" && (
            <div>
              <h3 className="text-[#1C3988] mb-3 font-bold">{t("media_inventory_title")}</h3>
              <p className="text-gray-500 mb-4 text-sm">{t("media_inventory_desc")}</p>
              {Object.keys(mediaInv).length === 0 ? <p className="text-gray-400 italic">{t("media_no_inventory")}</p> : (
                <div>{Object.entries(mediaInv).map(([key, d]) => {
                  const net = d.totalEntry - d.totalExit;
                  return (
                    <div key={key} className="attendance-row">
                      <div><strong>{d.item}</strong> ({d.desc})</div>
                      <div>{t("store_entries")} {d.totalEntry} | {t("store_exits")} {d.totalExit} | <strong>{t("media_current_balance")} {net}</strong></div>
                    </div>
                  );
                })}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TRANSFERS */}
      {mgmtTab === "transfers" && (
        <div className="card">
          <div className="mb-5 pb-3 border-b-2 border-gray-200">
            <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold"><ArrowLeftRight className="w-6 h-6" /> {t("transfers_title")}</h2>
          </div>
          <p className="text-gray-500 text-sm mb-5">{t("transfers_desc")}</p>

          <form onSubmit={addTransfer}>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-5">
              <div className="form-group">
                <label className="form-label">{t("lbl_from_school")}</label>
                <select className="form-input" value={trFromSchool} onChange={(e) => setTrFromSchool(e.target.value)} required>
                  <option value="">{t("ph_select_source")}</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_to_school")}</label>
                <select className="form-input" value={trToSchool} onChange={(e) => setTrToSchool(e.target.value)} required>
                  <option value="">{t("ph_select_dest")}</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_item")}</label>
                <input className="form-input" value={trItem} onChange={(e) => setTrItem(e.target.value)} required placeholder={t("ph_item")} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_description")}</label>
                <input className="form-input" value={trDesc} onChange={(e) => setTrDesc(e.target.value)} placeholder={t("ph_desc")} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_quantity")}</label>
                <input className="form-input" type="number" min="1" value={trQty} onChange={(e) => setTrQty(e.target.value)} required placeholder={t("ph_qty_num")} />
              </div>
            </div>
            <button type="submit" className="btn-primary"><ArrowLeftRight className="w-4 h-4" /> {t("btn_record_transfer")}</button>
          </form>

          <div className="mt-5">
            <h4 className="font-semibold mb-2">{t("transfers_all")}</h4>
            {transfers.length === 0 ? <p className="text-gray-400 italic">{t("transfers_none")}</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-200 text-left text-[#1C3988]">
                      <th className="py-2 px-2">{t("lbl_date")}</th>
                      <th className="py-2 px-2">{t("lbl_from")}</th>
                      <th className="py-2 px-2">{t("lbl_to")}</th>
                      <th className="py-2 px-2">{t("lbl_item")}</th>
                      <th className="py-2 px-2">{t("lbl_qty")}</th>
                      <th className="py-2 px-2">{t("lbl_status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map((tr) => (
                      <tr key={tr.id} className="border-b border-gray-100">
                        <td className="py-2 px-2 text-gray-500 whitespace-nowrap">{tr.transfer_date}</td>
                        <td className="py-2 px-2">{schools.find((s) => s.id === tr.from_school_id)?.name || "-"}</td>
                        <td className="py-2 px-2">{schools.find((s) => s.id === tr.to_school_id)?.name || "-"}</td>
                        <td className="py-2 px-2 font-semibold">{tr.item}</td>
                        <td className="py-2 px-2">{tr.quantity}</td>
                        <td className="py-2 px-2">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            tr.status === "completed" ? "bg-[#E8F5E9] text-[#2E7D32]" :
                            tr.status === "pending" ? "bg-[#FFF3E0] text-[#D97706]" :
                            "bg-[#FFEBEE] text-[#D32F2F]"
                          }`}>{tr.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SCHOOLS */}
      {mgmtTab === "schools" && (
        <div className="card">
          <div className="mb-5 pb-3 border-b-2 border-gray-200">
            <h2 className="text-[#1C3988] flex items-center gap-3 text-xl font-bold"><Building2 className="w-6 h-6" /> {t("schools_title")}</h2>
          </div>
          <p className="text-gray-500 text-sm mb-5">{t("schools_desc")}</p>

          <form onSubmit={addSchool}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div className="form-group">
                <label className="form-label">{t("lbl_school_name")}</label>
                <input className="form-input" value={newSchoolName} onChange={(e) => setNewSchoolName(e.target.value)} required placeholder={t("ph_school_name")} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_location")}</label>
                <input className="form-input" value={newSchoolLocation} onChange={(e) => setNewSchoolLocation(e.target.value)} placeholder={t("ph_location")} />
              </div>
              <div className="form-group">
                <label className="form-label">{t("lbl_school_desc")}</label>
                <input className="form-input" value={newSchoolDesc} onChange={(e) => setNewSchoolDesc(e.target.value)} placeholder={t("ph_school_desc")} />
              </div>
            </div>
            <button type="submit" className="btn-primary"><Plus className="w-4 h-4" /> {t("btn_add_school")}</button>
          </form>

          <div className="mt-5">
            <h4 className="font-semibold mb-2">{t("schools_all")} ({schools.length})</h4>
            {schools.length === 0 ? <p className="text-gray-400 italic">{t("schools_none")}</p> : (
              <div className="space-y-2">
                {schools.map((s) => (
                  <div key={s.id} className="attendance-row">
                    <div>
                      <strong className="text-[#1C3988]">{s.name}</strong>
                      {s.location && <span className="text-sm text-gray-500 ml-2">| {s.location}</span>}
                      {s.description && <span className="text-sm text-gray-400 ml-2">| {s.description}</span>}
                    </div>
                    <button className="btn-danger btn-sm" onClick={() => deleteSchool(s.id)}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
